import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Folder, FolderPlus, Plus, Trash2, ChevronRight, ChevronDown, Settings, Film, Link2, Heart, Layers, LayoutDashboard, Wrench, Sparkles, Scissors, X as XIcon } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { useMobileSidebar } from "@/contexts/MobileSidebarContext";

// Split root-level folders by their `category` (defaulting legacy rows to
// "library"). Descendants tag along with their root so subfolders never end up
// visually orphaned from their parent's section.
function partitionByCategory(folders) {
  const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
  const rootCategoryOf = (f) => {
    let cur = f;
    while (cur?.parent_id && byId[cur.parent_id]) cur = byId[cur.parent_id];
    return cur?.category || "library";
  };
  const library = [];
  const manager = [];
  for (const f of folders) {
    (rootCategoryOf(f) === "manager" ? manager : library).push(f);
  }
  return { library, manager };
}

function flattenTree(folders, expanded) {
  const byParent = {};
  folders.forEach((f) => {
    const key = f.parent_id || "_root";
    if (!byParent[key]) byParent[key] = [];
    byParent[key].push(f);
  });
  const result = [];
  const walk = (parentKey, depth) => {
    const list = byParent[parentKey] || [];
    for (const node of list) {
      const hasChildren = !!byParent[node.id]?.length;
      result.push({ ...node, depth, hasChildren });
      if (hasChildren && expanded[node.id]) walk(node.id, depth + 1);
    }
  };
  walk("_root", 0);
  return result;
}

export default function Sidebar({ folders, currentFolderId, onRefresh }) {
  const { user } = useAuth();
  // Iter 77 — Mobile drawer state. On lg+ this is ignored (sidebar is
  // always visible, position: sticky). On <lg the sidebar is a fixed
  // off-canvas drawer whose transform is driven by `open`.
  const { open: drawerOpen, setOpen: setDrawerOpen } = useMobileSidebar();
  // Iter 62 — Read the resolved brand so the header logo/wordmark/tagline
  // reflect whichever brand this user is bound to. Without this the CSS var
  // flip is invisible (nothing in the UI consumed useBrand() before).
  const brand = useBrand();
  // Iter 66 — Pinned collections shortcut. Fetch /collections once on mount
  // and refresh whenever the Collections page dispatches a change event.
  // No pinned collections ⇒ section is hidden entirely.
  const [pinnedCollections, setPinnedCollections] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/collections");
        if (!cancelled) setPinnedCollections((data || []).filter((c) => c.is_pinned));
      } catch (e) { /* silent — sidebar shouldn't nag on auth failures */ }
    };
    load();
    const onUpdate = () => load();
    window.addEventListener("wd:collections-updated", onUpdate);
    return () => { cancelled = true; window.removeEventListener("wd:collections-updated", onUpdate); };
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [creating, setCreating] = useState(null); // null | { parentId, category }
  const [newName, setNewName] = useState("");
  const [expanded, setExpanded] = useState({});

  // Role gates for iter 27 UI hardening:
  //   • admin  → full nav (Dashboard, All videos, Favorites, Collections) + folder CRUD
  //   • editor → only "All videos" nav link + tag/description edits (no folder CRUD)
  //   • client → no global nav (only their scope folder tree) + tag/description edits
  const isAdmin = user?.role === "admin";
  const isEditor = user?.role === "editor";
  // Iter 75 — clip-requests unread badge for admins/editors. Refetched on
  // mount and on the custom "wd:clip-requests-updated" event (fired by the
  // admin page after a PATCH/delete so the badge stays live).
  const [clipUnread, setClipUnread] = useState(0);
  useEffect(() => {
    if (!(isAdmin || isEditor)) return;
    const refetch = () => api.get("/admin/clip-requests/unread-count")
      .then((r) => setClipUnread(r.data?.count || 0))
      .catch(() => {});
    refetch();
    const h = () => refetch();
    window.addEventListener("wd:clip-requests-updated", h);
    return () => window.removeEventListener("wd:clip-requests-updated", h);
  }, [isAdmin, isEditor]);
  const canCreateFolder = isAdmin;    // admins alone reshape the tree
  const canEdit = canCreateFolder;    // legacy alias kept for local reads below
  const { library: libraryFolders, manager: managerFolders } = partitionByCategory(folders);
  const libraryFlat = flattenTree(libraryFolders, expanded);
  const managerFlat = flattenTree(managerFolders, expanded);

  // Auto-expand every ancestor of the current folder so it's visible on load
  useEffect(() => {
    if (!currentFolderId) return;
    const byId = Object.fromEntries(folders.map((f) => [f.id, f]));
    const toOpen = {};
    let cur = byId[currentFolderId];
    while (cur?.parent_id) {
      toOpen[cur.parent_id] = true;
      cur = byId[cur.parent_id];
    }
    if (Object.keys(toOpen).length) setExpanded((p) => ({ ...p, ...toOpen }));
  }, [currentFolderId, folders]);

  const createFolder = async (e) => {
    e.preventDefault();
    if (!creating || !newName.trim()) return;
    const { parentId, category } = creating;
    const body = { name: newName.trim() };
    if (parentId) body.parent_id = parentId;
    else body.category = category; // only root folders carry the section's category
    await api.post("/folders", body);
    if (parentId) setExpanded((p) => ({ ...p, [parentId]: true }));
    setNewName("");
    setCreating(null);
    onRefresh();
  };

  const deleteFolder = async (id, name, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"? The folder must be empty (no videos or subfolders inside).`)) return;
    try {
      await api.delete(`/folders/${id}`);
      if (currentFolderId === id) navigate("/");
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Could not delete folder");
    }
  };

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // Renders one titled section with its own "+" and folder tree. Reused for
  // both Libraries and Asset Manager. Section headers hide entirely when the
  // section has zero folders visible to this user (iter 31 requirement).
  const renderSection = ({ title, category, flat, testIdRoot }) => {
    if (!flat.length && !canEdit) return null; // clients/editors: hide empty sections
    const isCreatingRoot = creating && creating.parentId === null && creating.category === category;
    return (
      <div className="mb-2">
        <div className="px-4 pb-2 flex items-center justify-between border-t border-zinc-900/60 pt-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{title}</span>
          {canEdit && (
            <button
              data-testid={`new-${category}-root-btn`}
              onClick={() => { setCreating({ parentId: null, category }); setNewName(""); }}
              className="text-zinc-500 hover:text-[var(--brand-accent)] transition p-1"
              title={`New root-level ${title} folder`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {isCreatingRoot && (
          <form onSubmit={createFolder} className="px-4 mb-2">
            <input
              data-testid={`new-${category}-root-input`}
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") setCreating(null); }}
              onBlur={() => { if (!newName) setCreating(null); }}
              placeholder="Folder name"
              className="w-full bg-black/40 border border-zinc-800 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60"
            />
            <button type="submit" className="hidden">submit</button>
          </form>
        )}

        <ul className="space-y-0.5" data-testid={testIdRoot}>
          {flat.map((n) => {
            const isActive = currentFolderId === n.id;
            const isOpen = expanded[n.id];
            return (
              <li key={n.id}>
                <Link
                  to={`/folders/${n.id}`}
                  data-testid={TID.folderItem(n.id)}
                  className={`group flex items-center gap-1.5 pr-3 py-1.5 text-sm transition ${
                    isActive ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40"
                  }`}
                  style={{ paddingLeft: `${16 + n.depth * 12}px` }}
                >
                  {n.hasChildren ? (
                    <button
                      onClick={(e) => { e.preventDefault(); toggle(n.id); }}
                      className="text-zinc-600 hover:text-zinc-300"
                    >
                      {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                  ) : (
                    <span className="w-3.5" />
                  )}
                  <Folder className={`w-3.5 h-3.5 ${isActive ? "text-[var(--brand-accent)]" : "text-zinc-500"}`} />
                  <span className="truncate flex-1">{n.name}</span>
                  {n.asset_count > 0 && (
                    <span className="text-[10px] text-zinc-600">{n.asset_count}</span>
                  )}
                  {canEdit && (
                    <>
                      <button
                        data-testid={`add-subfolder-btn-${n.id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded((p) => ({ ...p, [n.id]: true })); setCreating({ parentId: n.id, category }); setNewName(""); }}
                        className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-[var(--brand-accent)] transition"
                        title="Add subfolder"
                      >
                        <FolderPlus className="w-3 h-3" />
                      </button>
                      <button
                        data-testid={TID.deleteFolderBtn(n.id)}
                        onClick={(e) => deleteFolder(n.id, n.name, e)}
                        className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition"
                        title="Delete (empty folders only)"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </Link>
                {creating && creating.parentId === n.id && (
                  <form
                    onSubmit={createFolder}
                    className="pr-3 py-1"
                    style={{ paddingLeft: `${16 + (n.depth + 1) * 12 + 14}px` }}
                  >
                    <input
                      autoFocus
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") setCreating(null); }}
                      onBlur={() => { if (!newName) setCreating(null); }}
                      placeholder="Subfolder name"
                      className="w-full bg-black/40 border border-zinc-800 rounded px-2 py-1 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60"
                      data-testid={`new-subfolder-input-${n.id}`}
                    />
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      {/* Iter 77 — Mobile drawer backdrop. Only renders when open on <lg.
          Tap outside the sidebar to dismiss. Fully hidden on lg+ via the
          `lg:hidden` modifier so desktop keeps its static sidebar layout. */}
      {drawerOpen && (
        <div
          data-testid="sidebar-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          aria-hidden
        />
      )}
      <aside
        data-testid={TID.sidebar}
        className={`w-[280px] shrink-0 border-r border-zinc-800/60 bg-[#09090b] flex flex-col h-screen transition-transform duration-200 ease-out
          fixed inset-y-0 left-0 z-50 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}
          lg:static lg:sticky lg:top-0 lg:translate-x-0`}
      >
        {/* Iter 77 — Close affordance only on mobile. Desktop never shows it. */}
        <button
          type="button"
          data-testid="sidebar-drawer-close"
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden absolute top-3 right-3 w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-[var(--brand-accent)]"
          aria-label="Close navigation"
        >
          <XIcon className="w-4 h-4" />
        </button>
      {/* Iter 63 — Bigger logo, no wordmark/tagline. Gives each brand's
          logo real room to breathe. Image logos are capped at max-w-[220px]
          + object-contain so a wide brand (Bass Pro's oval + CLUB stack)
          never crowds the 280px sidebar. Brands without a logo fall back
          to a color-blocked wordmark chip.
          Iter 64 — Header raised to h-[128px] / logo h-16 so Bass Pro's
          "— CLUB —" text underneath the oval is legible (~16px tall).
          Logos are left-justified per user preference. */}
      <div
        data-testid="sidebar-brand-header"
        data-brand={brand.id}
        className="px-6 py-5 border-b border-zinc-800/60 flex items-center h-[128px]"
      >
        <Link to="/" className="flex items-center hover:opacity-80 transition min-w-0">
          {brand.logoUrl ? (
            <img
              src={brand.logoUrl}
              alt={brand.short}
              className="h-16 w-auto max-w-[220px] object-contain object-left"
              data-testid="sidebar-brand-logo"
            />
          ) : (
            <div
              data-testid="sidebar-brand-wordmark"
              className="h-16 rounded-md px-4 flex items-center text-base font-black tracking-[0.15em] text-black max-w-[220px] truncate"
              style={{ backgroundColor: brand.accent }}
              title={brand.name}
            >
              {(brand.wordmark || brand.short).toUpperCase()}
            </div>
          )}
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-4 mb-3 space-y-0.5">
          {isAdmin && (
            <Link
              to="/"
              className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm transition ${
                location.pathname === "/" ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40"
              }`}
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
              <span>Dashboard</span>
            </Link>
          )}
          {(isAdmin || isEditor) && (
            <Link
              to="/library"
              className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm transition ${
                location.pathname === "/library" ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40"
              }`}
            >
              <Film className="w-3.5 h-3.5" />
              <span>All videos</span>
            </Link>
          )}
          {isAdmin && (
            <Link
              data-testid={TID.favoritesLink}
              to="/favorites"
              className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm transition ${
                location.pathname === "/favorites" ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40"
              }`}
            >
              <Heart className="w-3.5 h-3.5" />
              <span>Favorites</span>
            </Link>
          )}
          {(isAdmin || user?.role === "editor" || user?.role === "client") && (
            <Link
              data-testid={TID.collectionsLink}
              to="/collections"
              className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm transition ${
                location.pathname.startsWith("/collections") ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40"
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Collections</span>
            </Link>
          )}
        </div>

        {/* Iter 66 — Pinned collections shortcut. Renders only when the user
            has pinned ≥1 collection. Smart collections show the Sparkles
            icon, regular collections show Layers. Section header mirrors
            the "REVIEW LIBRARIES" / "ASSET MANAGER" pattern. */}
        {(isAdmin || user?.role === "editor" || user?.role === "client") && pinnedCollections.length > 0 && (
          <div className="mt-4" data-testid="sidebar-pinned-collections">
            <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-zinc-600">Pinned</div>
            <div className="space-y-0.5">
              {pinnedCollections.map((c) => {
                const active = location.pathname === `/collections/${c.id}`;
                const Icon = c.is_smart ? Sparkles : Layers;
                return (
                  <Link
                    key={c.id}
                    to={`/collections/${c.id}`}
                    data-testid={`sidebar-pinned-${c.id}`}
                    data-smart={c.is_smart ? "true" : "false"}
                    title={c.is_smart ? `${c.name} — smart collection` : c.name}
                    className={`flex items-center gap-2 pl-6 pr-2 py-1 rounded text-sm transition ${
                      active ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40"
                    }`}
                  >
                    <Icon className={`w-3 h-3 shrink-0 ${c.is_smart ? "text-[var(--brand-accent)]" : "text-zinc-500"}`} />
                    <span className="truncate flex-1">{c.name}</span>
                    <span className="text-[10px] text-zinc-600 shrink-0">{c.asset_count}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {renderSection({
          title: "Review Libraries",
          category: "library",
          flat: libraryFlat,
          testIdRoot: TID.sidebar + "-libraries-tree",
        })}
        {renderSection({
          title: "Asset Manager",
          category: "manager",
          flat: managerFlat,
          testIdRoot: TID.sidebar + "-manager-tree",
        })}

        {folders.length === 0 && !creating && (
          <div className="px-4 py-8 text-center">
            <Film className="w-8 h-8 mx-auto text-zinc-700 mb-2" />
            <p className="text-xs text-zinc-600">No folders yet</p>
            {canEdit && (
              <button
                onClick={() => setCreating({ parentId: null, category: "library" })}
                className="text-xs text-[var(--brand-accent)] hover:underline mt-2"
              >
                Create your first folder
              </button>
            )}
          </div>
        )}

        {user?.role === "admin" && (
          <div className="border-t border-zinc-800/60 mt-4 pt-4 px-4 space-y-1">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Admin</span>
            <Link
              data-testid="clip-requests-link"
              to="/clip-requests"
              className={`flex items-center gap-2 px-2 py-2 rounded text-sm transition ${
                location.pathname.startsWith("/clip-requests") ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Scissors className="w-4 h-4" />
              <span className="flex-1">Clip requests</span>
              {clipUnread > 0 && (
                <span data-testid="clip-requests-badge" className="text-[10px] font-semibold bg-[var(--brand-accent)] text-black rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">{clipUnread}</span>
              )}
            </Link>
            <Link
              data-testid={TID.sharesLink}
              to="/shares"
              className={`flex items-center gap-2 px-2 py-2 rounded text-sm transition ${
                location.pathname.startsWith("/shares") ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Link2 className="w-4 h-4" />
              <span>Share links</span>
            </Link>
            <Link
              data-testid={TID.adminLink}
              to="/admin"
              className={`flex items-center gap-2 px-2 py-2 rounded text-sm transition ${
                location.pathname.startsWith("/admin") ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Settings className="w-4 h-4" />
              <span>Users &amp; tags</span>
            </Link>
          </div>
        )}
        {user?.role === "editor" && (
          <div className="border-t border-zinc-800/60 mt-4 pt-4 px-4">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Tools</span>
            <Link
              data-testid="clip-requests-link"
              to="/clip-requests"
              className={`flex items-center gap-2 px-2 py-2 mt-2 rounded text-sm transition ${
                location.pathname.startsWith("/clip-requests") ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Scissors className="w-4 h-4" />
              <span className="flex-1">Clip requests</span>
              {clipUnread > 0 && (
                <span data-testid="clip-requests-badge" className="text-[10px] font-semibold bg-[var(--brand-accent)] text-black rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">{clipUnread}</span>
              )}
            </Link>
            <Link
              data-testid={TID.sharesLink}
              to="/shares"
              className={`flex items-center gap-2 px-2 py-2 mt-2 rounded text-sm transition ${
                location.pathname.startsWith("/shares") ? "sidebar-item-active text-[var(--brand-accent)]" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Link2 className="w-4 h-4" />
              <span>Share links</span>
            </Link>
          </div>
        )}
      </nav>
    </aside>
    </>
  );
}
